/** 発送予定詳細パネルの編集フィールド */
export const SHIPMENT_PLAN_DETAIL_FIELD_NAMES = [
  'shipment_date',
  'adjustment_memo',
  'shipping_preparation',
  'pl_qty',
  'package_qty',
  'desired_shipment_date',
  'desired_arrival_date',
  'comment',
  'order_check',
  'w_check',
  'cancel_return_issue',
  'shipping_memo',
  'transport_service',
  'tracking_number',
];

/** PL数未入力時の画面上の表示（DB は NULL のまま） */
export const PL_QTY_EMPTY_DISPLAY = '0';

export const EMPTY_SHIPMENT_PLAN_DRAFT = Object.freeze(
  SHIPMENT_PLAN_DETAIL_FIELD_NAMES.reduce((acc, name) => {
    acc[name] = name === 'pl_qty' ? PL_QTY_EMPTY_DISPLAY : '';
    return acc;
  }, {})
);

const DATE_FIELDS = new Set([
  'shipment_date',
  'desired_shipment_date',
  'desired_arrival_date',
]);

const SELECT_FIELDS = new Set([
  'shipping_preparation',
  'package_qty',
  'order_check',
  'w_check',
  'cancel_return_issue',
  'transport_service',
]);

const TEXT_FIELDS = new Set([
  'adjustment_memo',
  'comment',
  'shipping_memo',
]);

export function formatShipmentPlanDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
}

export function rowToShipmentPlanDraft(row, formatDate = formatShipmentPlanDate) {
  if (!row) {
    return { ...EMPTY_SHIPMENT_PLAN_DRAFT };
  }
  const shipmentDateSource = row.shipment_date || row.latest_shipment_date;
  return {
    shipment_date: formatDate(shipmentDateSource) || '',
    adjustment_memo: row.adjustment_memo || '',
    shipping_preparation: row.shipping_preparation || '',
    pl_qty:
      row.pl_qty !== null && row.pl_qty !== undefined
        ? String(row.pl_qty)
        : PL_QTY_EMPTY_DISPLAY,
    package_qty: row.package_qty && row.package_qty !== '0' ? row.package_qty : '',
    desired_shipment_date: formatDate(row.desired_shipment_date) || '',
    desired_arrival_date: formatDate(row.desired_arrival_date) || '',
    comment: row.comment || '',
    order_check: row.order_check || '',
    w_check: row.w_check || '',
    cancel_return_issue: row.cancel_return_issue || '',
    shipping_memo: row.shipping_memo || '',
    transport_service: row.transport_service || '',
    tracking_number: row.tracking_number || '',
  };
}

export function normalizePlQty(raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  if (Number.isNaN(n) || n <= 0) return null;
  return n;
}

export function normalizeSelect(raw) {
  if (raw === '' || raw === null || raw === undefined || raw === '0') return null;
  return raw;
}

export function normalizeText(raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  return raw;
}

export function normalizeDate(raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  return raw;
}

export function normalizeTrackingNumberInput(raw) {
  if (raw === '' || raw === null || raw === undefined) return { display: '', value: null, valid: true };
  const str = String(raw);
  if (!/^\d+$/.test(str)) {
    return { display: str, value: null, valid: false };
  }
  return { display: str, value: str, valid: true };
}

export function normalizeFieldForApi(field, rawValue) {
  if (field === 'pl_qty') return normalizePlQty(rawValue);
  if (field === 'package_qty') return normalizeSelect(rawValue);
  if (field === 'tracking_number') {
    const { value, valid } = normalizeTrackingNumberInput(rawValue);
    if (!valid) return { error: '送り状Noは数値のみ入力可能です' };
    return value;
  }
  if (DATE_FIELDS.has(field)) return normalizeDate(rawValue);
  if (SELECT_FIELDS.has(field)) return normalizeSelect(rawValue);
  if (TEXT_FIELDS.has(field)) return normalizeText(rawValue);
  return rawValue === '' ? null : rawValue;
}

export function draftDisplayValue(field, savedValue) {
  if (field === 'pl_qty') {
    if (savedValue === null || savedValue === undefined) return PL_QTY_EMPTY_DISPLAY;
    return String(savedValue);
  }
  if (savedValue === null || savedValue === undefined) return '';
  if (DATE_FIELDS.has(field)) return formatShipmentPlanDate(savedValue);
  if (field === 'package_qty' && savedValue === '0') return '';
  return String(savedValue);
}

export function savedValueFromDraft(field, draftValue) {
  const normalized = normalizeFieldForApi(field, draftValue);
  if (normalized && typeof normalized === 'object' && normalized.error) {
    return undefined;
  }
  return normalized;
}

export function isSameDraftValue(field, a, b) {
  const normA = normalizeFieldForApi(field, a);
  const normB = normalizeFieldForApi(field, b);
  if (normA && typeof normA === 'object' && normA.error) return false;
  if (normB && typeof normB === 'object' && normB.error) return false;
  return normA === normB;
}

export function buildSingleFieldPatch(field, rawValue) {
  const normalized = normalizeFieldForApi(field, rawValue);
  if (normalized && typeof normalized === 'object' && normalized.error) {
    return { error: normalized.error };
  }
  return { [field]: normalized };
}

export function buildPlPackagePatch(plQtyRaw, packageQtyRaw) {
  const plQty = normalizePlQty(plQtyRaw);
  const packageQty = normalizeSelect(packageQtyRaw);

  if (plQty !== null) {
    return { pl_qty: plQty, package_qty: null };
  }
  if (packageQty !== null) {
    return { pl_qty: null, package_qty: packageQty };
  }
  return { pl_qty: null, package_qty: null };
}

export function arePlPackageDraftsDirty(draft, saved) {
  const draftPatch = buildPlPackagePatch(draft.pl_qty, draft.package_qty);
  const savedPatch = buildPlPackagePatch(saved.pl_qty, saved.package_qty);
  return draftPatch.pl_qty !== savedPatch.pl_qty || draftPatch.package_qty !== savedPatch.package_qty;
}

export function applyPlPackagePatchToDraft(draft) {
  const patch = buildPlPackagePatch(draft.pl_qty, draft.package_qty);
  return {
    ...draft,
    pl_qty: patch.pl_qty === null ? PL_QTY_EMPTY_DISPLAY : String(patch.pl_qty),
    package_qty: patch.package_qty || '',
  };
}

export function isPlQtyActive(draft) {
  return normalizePlQty(draft.pl_qty) !== null;
}

export function getShipmentPlanRowKey(row) {
  if (!row) return '';
  return `${row.hacchu_code}-${row.hacchu_changecnt}-${row.hacchu_datacode}`;
}

/** 発注No一括操作で反映可能な項目（PL数・個口は除外） */
export const PURCHASE_ORDER_BULK_FIELD_DEFS = [
  { name: 'shipment_date', label: '発送日', type: 'date' },
  { name: 'shipping_preparation', label: '発送準備', type: 'select', options: ['済', '入荷', '工場在庫', 'メーカー直送'] },
  { name: 'desired_shipment_date', label: '発送希望日', type: 'date' },
  { name: 'desired_arrival_date', label: '着希望日', type: 'date' },
  { name: 'transport_service', label: '運送便', type: 'transport_service' },
  { name: 'adjustment_memo', label: '調整メモ', type: 'textarea', layout: 'full' },
  { name: 'comment', label: 'コメント', type: 'textarea', layout: 'full' },
  { name: 'shipping_memo', label: '送料メモ', type: 'textarea', layout: 'full' },
  { name: 'tracking_number', label: '送り状No', type: 'text' },
  { name: 'order_check', label: '受注チェック', type: 'select', options: ['済', '未'] },
  {
    name: 'w_check',
    label: 'Wチェック',
    type: 'select',
    options: [
      '工場出荷',
      'チェック済',
      '宝機材製に変更案内文OK',
      '工場渡し',
      'トラック便',
      '発送ストップ',
      '営業便',
      '資材倉庫渡し',
    ],
  },
  {
    name: 'cancel_return_issue',
    label: 'キャンセル・返品・不具合',
    type: 'select',
    options: [
      'キャンセル',
      '返品',
      '交換',
      '不具合が発生',
      '副愛品の代替品',
      '残貨',
      'サンプル',
    ],
  },
];

const BULK_FIELD_NAME_SET = new Set(PURCHASE_ORDER_BULK_FIELD_DEFS.map((def) => def.name));

/** 一括操作時に追記するメモ系フィールド */
export const BULK_MEMO_APPEND_FIELDS = new Set([
  'adjustment_memo',
  'comment',
  'shipping_memo',
]);

export const EMPTY_BULK_DRAFT = Object.freeze(
  PURCHASE_ORDER_BULK_FIELD_DEFS.reduce((acc, def) => {
    acc[def.name] = '';
    return acc;
  }, {})
);

export function getBulkFieldLabel(fieldName) {
  const def = PURCHASE_ORDER_BULK_FIELD_DEFS.find((item) => item.name === fieldName);
  return def ? def.label : fieldName;
}

/** 明細選択チェック数から一括適用対象を決定（UIでは常に selected、未選択時はバリデーションで阻止） */
export function resolveBulkTargetType(selectedCount) {
  return selectedCount > 0 ? 'selected' : 'selected';
}

export function getBulkTargetSummaryLabel(selectedCount) {
  if (selectedCount > 0) {
    return `選択した${selectedCount}件の明細`;
  }
  return '明細を選択してください';
}

/** 操作済み（touched）項目を定義順で apply_fields 用に収集 */
export function collectBulkApplyFields(touchedFields) {
  const touched = touchedFields instanceof Set ? touchedFields : new Set(touchedFields);
  return PURCHASE_ORDER_BULK_FIELD_DEFS.map((def) => def.name).filter((name) => touched.has(name));
}

/** touched から実際に反映する項目を決定（メモ空欄は追記なしのため除外） */
export function resolveEffectiveBulkApplyFields(touchedFields, draft) {
  return collectBulkApplyFields(touchedFields).filter((field) => {
    if (!BULK_MEMO_APPEND_FIELDS.has(field)) return true;
    const normalized = normalizeFieldForApi(field, draft[field]);
    return normalized !== null && normalized !== '';
  });
}

export function appendBulkMemoValue(existing, appendText) {
  if (!appendText) return existing || '';
  if (!existing) return appendText;
  return `${existing}\n${appendText}`;
}

function formatBulkFieldDisplayValue(fieldName, normalizedValue) {
  if (normalizedValue === null || normalizedValue === undefined) {
    return '（クリア）';
  }
  if (DATE_FIELDS.has(fieldName)) {
    const formatted = formatShipmentPlanDate(normalizedValue);
    return formatted || String(normalizedValue);
  }
  return String(normalizedValue);
}

export function formatBulkFieldPreviewValue(fieldName, draftValue, existingValue) {
  const normalized = normalizeFieldForApi(fieldName, draftValue);
  if (BULK_MEMO_APPEND_FIELDS.has(fieldName)) {
    if (normalized === null || normalized === '') return null;
    return appendBulkMemoValue(existingValue || '', normalized);
  }
  return formatBulkFieldDisplayValue(fieldName, normalized);
}

export function buildBulkConfirmPreview({
  targetDetails,
  draft,
  applyFields,
  containsSplitPlans,
}) {
  return {
    detailItems: (targetDetails || []).map((detail) => ({
      datacode: detail.hacchu_datacode,
      productName: detail.product_name || '-',
      productSize: detail.product_size || '-',
      fields: applyFields
        .map((fieldName) => {
          const displayValue = formatBulkFieldPreviewValue(
            fieldName,
            draft[fieldName],
            detail[fieldName]
          );
          if (displayValue === null) return null;
          return {
            name: fieldName,
            label: getBulkFieldLabel(fieldName),
            displayValue,
            isAppend: BULK_MEMO_APPEND_FIELDS.has(fieldName),
          };
        })
        .filter(Boolean),
    })),
    showSplitWarning: Boolean(containsSplitPlans && applyFields.includes('shipment_date')),
  };
}

export function validateBulkBeforeSubmit(draft, applyFields, selectedCount) {
  if (!selectedCount || selectedCount < 1) {
    return '1件以上の明細を選択してください';
  }
  if (!applyFields || applyFields.length === 0) {
    return '1つ以上の項目を入力・変更してください';
  }
  if (applyFields.includes('tracking_number') && draft.tracking_number) {
    const normalized = normalizeFieldForApi('tracking_number', draft.tracking_number);
    if (normalized && typeof normalized === 'object' && normalized.error) {
      return normalized.error;
    }
  }
  return null;
}

export function buildBulkUpdatePayload({
  draft,
  applyFields,
  targetType,
  selectedDatacodes,
}) {
  const payload = {
    target_type: targetType,
    selected_datacodes: targetType === 'selected' ? Array.from(selectedDatacodes) : null,
    apply_fields: applyFields,
  };

  applyFields.forEach((field) => {
    if (!BULK_FIELD_NAME_SET.has(field)) return;
    const normalized = normalizeFieldForApi(field, draft[field]);
    if (normalized && typeof normalized === 'object' && normalized.error) {
      throw new Error(normalized.error);
    }
    payload[field] = normalized;
  });

  return payload;
}
